import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ChatUnreadEntry {
  conversationId: string;
  count: number;
  name: string;
  avatarUrl: string | null;
  lastActivity: string;
  lastContent: string | null;
  /** ID of the most-recent message that triggered this unread entry.
   *  Used by the notifications popover to jump-to-message when the row
   *  is clicked (so the chat opens at the new message, not the bottom). */
  lastMessageId?: string;
}

/** A read-historical record of a previously-unread chat. Powers the
 *  "All" view in the notifications panel so users can revisit recent
 *  activity even after marking it read. Snapshot of the unread entry
 *  at the moment it was cleared + the timestamp when it was read. */
export interface ChatHistoryEntry extends ChatUnreadEntry {
  readAt: string;
}

interface State {
  byId: Record<string, ChatUnreadEntry>;
  hydrated: boolean;
  /** Conversation IDs the user has explicitly marked read.
   *  Persisted to localStorage so app restarts/reloads can't resurface
   *  stale unread counts the server may still report. Capped at MAX_CLEARED
   *  entries (FIFO eviction) to bound storage growth. */
  clearedIds: string[];
  /** Snapshot log of recently-cleared entries, newest first. Bounded by
   *  MAX_HISTORY. Persisted to localStorage so the All view survives reloads. */
  history: ChatHistoryEntry[];
}

const CLEARED_STORAGE_KEY = 'gc.chatUnread.cleared.v1';
const HISTORY_STORAGE_KEY = 'gc.chatUnread.history.v1';
const MAX_CLEARED = 1000;
const MAX_HISTORY = 100;

function loadClearedIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CLEARED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

function persistClearedIds(ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CLEARED_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* quota or private mode — ignore */
  }
}

function pushCleared(state: State, id: string): void {
  if (state.clearedIds.includes(id)) return;
  state.clearedIds.push(id);
  while (state.clearedIds.length > MAX_CLEARED) state.clearedIds.shift();
  persistClearedIds(state.clearedIds);
}

function loadHistory(): ChatHistoryEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (v): v is ChatHistoryEntry =>
        v && typeof v.conversationId === 'string' && typeof v.readAt === 'string',
    );
  } catch {
    return [];
  }
}

function persistHistory(items: ChatHistoryEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota or private mode — ignore */
  }
}

function pushHistory(state: State, entry: ChatUnreadEntry): void {
  // Latest read first; replace any older snapshot for the same conv so the
  // history list stays one-entry-per-conversation.
  state.history = [
    { ...entry, readAt: new Date().toISOString() },
    ...state.history.filter(h => h.conversationId !== entry.conversationId),
  ].slice(0, MAX_HISTORY);
  persistHistory(state.history);
}

const initialState: State = {
  byId: {},
  hydrated: false,
  clearedIds: loadClearedIds(),
  history: loadHistory(),
};

const slice = createSlice({
  name: 'chatUnread',
  initialState,
  reducers: {
    bulkHydrate(state, action: PayloadAction<ChatUnreadEntry[]>) {
      state.byId = {};
      for (const e of action.payload) {
        // Don't restore entries for conversations the user already cleared.
        if (!state.clearedIds.includes(e.conversationId)) {
          state.byId[e.conversationId] = e;
        }
      }
      state.hydrated = true;
    },
    incrementForConversation(
      state,
      action: PayloadAction<{
        conversationId: string;
        lastActivity: string;
        name?: string;
        avatarUrl?: string | null;
        content?: string | null;
        lastMessageId?: string;
      }>
    ) {
      const { conversationId, lastActivity, name, avatarUrl, content, lastMessageId } = action.payload;
      // A new incoming message re-activates the conversation even if cleared before.
      if (state.clearedIds.includes(conversationId)) {
        state.clearedIds = state.clearedIds.filter(id => id !== conversationId);
        persistClearedIds(state.clearedIds);
      }
      // Drop any history snapshot — this conv is unread again, so the read
      // record is no longer accurate and would be a duplicate alongside the
      // new unread entry.
      if (state.history.some(h => h.conversationId === conversationId)) {
        state.history = state.history.filter(h => h.conversationId !== conversationId);
        persistHistory(state.history);
      }
      const existing = state.byId[conversationId];
      if (existing) {
        existing.count += 1;
        existing.lastActivity = lastActivity;
        if (content !== undefined) existing.lastContent = content;
        if (name && existing.name === 'Conversation') existing.name = name;
        if (avatarUrl && !existing.avatarUrl) existing.avatarUrl = avatarUrl;
        // Always advance to the latest triggering message so the popover
        // jumps to the freshest one rather than the first one we saw.
        if (lastMessageId) existing.lastMessageId = lastMessageId;
      } else {
        state.byId[conversationId] = {
          conversationId,
          count: 1,
          name: name ?? 'Conversation',
          avatarUrl: avatarUrl ?? null,
          lastActivity,
          lastContent: content ?? null,
          lastMessageId,
        };
      }
    },
    clearConversation(state, action: PayloadAction<string>) {
      const existing = state.byId[action.payload];
      if (existing) pushHistory(state, existing);
      delete state.byId[action.payload];
      pushCleared(state, action.payload);
    },
    clearAll(state) {
      for (const id of Object.keys(state.byId)) {
        pushHistory(state, state.byId[id]);
        pushCleared(state, id);
      }
      state.byId = {};
    },
    /** Remove a single read-history entry (used by the panel's per-row trash
     *  action in All view). Does not touch unread state. */
    removeHistoryEntry(state, action: PayloadAction<string>) {
      const before = state.history.length;
      state.history = state.history.filter(h => h.conversationId !== action.payload);
      if (state.history.length !== before) persistHistory(state.history);
    },
    /** Wipe the entire read-history log (used by the panel's "Clear all" in
     *  All view). Does not touch unread state. */
    clearHistory(state) {
      if (state.history.length === 0) return;
      state.history = [];
      persistHistory(state.history);
    },
  },
});

export const {
  bulkHydrate,
  incrementForConversation,
  clearConversation,
  clearAll,
  removeHistoryEntry,
  clearHistory,
} = slice.actions;

export const selectUnreadEntries = (s: any): ChatUnreadEntry[] => {
  const byId = (s.chatUnread as State).byId;
  return Object.values(byId).sort((a, b) =>
    a.lastActivity < b.lastActivity ? 1 : -1
  );
};

export const selectHistory = (s: any): ChatHistoryEntry[] =>
  (s.chatUnread as State).history;

/** Combined Unread + History feed for the panel's All view. Unread entries
 *  come first (most recent activity at top), then history entries (most
 *  recently read at top). */
export interface ChatPanelEntry extends ChatUnreadEntry {
  /** Set when this row came from the read-history log. */
  readAt?: string;
}

export const selectAllPanelEntries = (s: any): ChatPanelEntry[] => {
  const st = s.chatUnread as State;
  const unread: ChatPanelEntry[] = Object.values(st.byId).sort((a, b) =>
    a.lastActivity < b.lastActivity ? 1 : -1,
  );
  // History is already newest-first by readAt; preserve that ordering.
  const history: ChatPanelEntry[] = st.history.map(h => ({ ...h }));
  return [...unread, ...history];
};

export const selectTotalUnread = (s: any): number =>
  Object.values((s.chatUnread as State).byId).reduce(
    (sum, e) => sum + e.count,
    0
  );

export const selectChatUnreadHydrated = (s: any): boolean =>
  (s.chatUnread as State).hydrated;

export const selectUnreadById = (s: any): Record<string, ChatUnreadEntry> =>
  (s.chatUnread as State).byId;

export const selectClearedIds = (s: any): string[] =>
  (s.chatUnread as State).clearedIds;

export default slice.reducer;
